/**
 * Page anchor navigation: smooth scroll to targets,
 * pins the bar under the site header (teleports to body to escape #main-content clip-path).
 */
if (!customElements.get('page-anchor-nav')) {
  class PageAnchorNav extends HTMLElement {
    connectedCallback() {
      this.wrapper = this.querySelector('.page-anchor-nav__wrapper');
      this.track = this.querySelector('.page-anchor-nav__track');
      this.placeholder = this.querySelector('.page-anchor-nav__placeholder');
      this.buttons = Array.from(this.querySelectorAll('[data-anchor-target]'));
      this.extraTop = parseInt(this.dataset.extraOffset || '0', 10) || 0;
      this.smooth = this.dataset.smooth !== 'false';
      this.pinned = false;
      this._unpinScrollY = 0;
      this._activeLock = false;
      this._unlockTimer = null;
      this._trackHomeParent = null;
      this._trackHomeNext = null;

      /** 用 header 可视底边计算吸顶 top，避免 CSS 变量含公告栏导致偏大 */
      this._readPinTop = () => {
        const headerSection = document.querySelector('.header-section');
        if (headerSection) {
          const bottom = headerSection.getBoundingClientRect().bottom;
          return Math.max(0, Math.round(bottom) + this.extraTop);
        }
        const root = document.documentElement;
        const h = parseInt(getComputedStyle(root).getPropertyValue('--header-height'), 10) || 0;
        const ann = parseInt(getComputedStyle(root).getPropertyValue('--announcement-height'), 10) || 0;
        return Math.max(0, h + ann + this.extraTop);
      };

      this._applyPinTopVar = (pinTop) => {
        const value = `${pinTop}px`;
        this.style.setProperty('--anchor-nav-pin-top', value);
        if (this.track) {
          this.track.style.setProperty('--anchor-nav-pin-top', value);
          if (this.pinned) {
            this.track.style.top = value;
          }
        }
      };

      this._measureWrapperDocTop = () => {
        if (!this.wrapper) return 0;
        const r = this.wrapper.getBoundingClientRect();
        return r.top + window.scrollY;
      };

      this._syncLayout = () => {
        if (!this.wrapper || !this.track) return;
        const pinTop = this._readPinTop();
        this._applyPinTopVar(pinTop);
        if (!this.pinned) {
          this._unpinScrollY = this._measureWrapperDocTop() - pinTop;
        }
        if (this.pinned && this.placeholder) {
          this.placeholder.style.height = `${this.track.offsetHeight}px`;
        }
      };

      this._setPinned = (next) => {
        if (next === this.pinned || !this.track || !this.placeholder) return;
        this.pinned = next;
        const pinTop = this._readPinTop();

        if (next) {
          /* 移出 #main-content：主题 clip-path 会使 fixed 相对内容区而非视口 */
          this._trackHomeParent = this.track.parentNode;
          this._trackHomeNext = this.track.nextSibling;
          this.placeholder.style.height = `${this.track.offsetHeight}px`;
          document.body.appendChild(this.track);
          this.track.classList.add('is-pinned');
          this._applyPinTopVar(pinTop);
        } else {
          this.track.classList.remove('is-pinned');
          this.track.style.top = '';
          const parent = this._trackHomeParent;
          const nextNode = this._trackHomeNext;
          if (parent) {
            if (nextNode && nextNode.parentNode === parent) {
              parent.insertBefore(this.track, nextNode);
            } else {
              parent.appendChild(this.track);
            }
          }
          this._trackHomeParent = null;
          this._trackHomeNext = null;
          this.placeholder.style.height = '';
          requestAnimationFrame(() => this._syncLayout());
        }
      };

      this._setActiveButton = (activeBtn) => {
        this.buttons.forEach((b) => b.classList.toggle('is-active', b === activeBtn));
      };

      this._clearActiveLock = () => {
        if (this._unlockTimer) {
          window.clearTimeout(this._unlockTimer);
          this._unlockTimer = null;
        }
        if (this._onScrollEndBound) {
          window.removeEventListener('scrollend', this._onScrollEndBound);
          this._onScrollEndBound = null;
        }
        this._activeLock = false;
      };

      this._lockActiveUntilScrollSettles = (btn) => {
        this._clearActiveLock();
        this._activeLock = true;
        this._setActiveButton(btn);

        const unlock = () => {
          this._clearActiveLock();
          this._updateActiveFromScroll();
        };

        if (!this.smooth) {
          unlock();
          return;
        }

        this._onScrollEndBound = unlock;
        window.addEventListener('scrollend', this._onScrollEndBound, { once: true });
        this._unlockTimer = window.setTimeout(unlock, 900);
      };

      this._onScroll = () => {
        if (!this.wrapper || !this.track) return;
        const pinTop = this._readPinTop();
        this._applyPinTopVar(pinTop);
        const scrollY = window.scrollY || window.pageYOffset;

        if (!this.pinned) {
          this._unpinScrollY = this._measureWrapperDocTop() - pinTop;
          const top = this.wrapper.getBoundingClientRect().top;
          if (top <= pinTop + 0.5) {
            this._setPinned(true);
          }
        } else if (scrollY < this._unpinScrollY - 1) {
          this._setPinned(false);
        }

        this._updateActiveFromScroll();
      };

      this._onResize = () => {
        this._syncLayout();
        this._onScroll();
      };

      this._parseTargetId = (raw) => {
        const id = (raw || '').replace(/^#/, '').trim();
        if (!id || /[\s#]/.test(id)) return '';
        return id;
      };

      this._onClick = (e) => {
        const btn = e.target.closest('[data-anchor-target]');
        if (!btn || !this.track || !this.track.contains(btn)) return;
        e.preventDefault();
        const id = this._parseTargetId(btn.getAttribute('data-anchor-target'));
        if (!id) return;
        const el = document.getElementById(id);
        if (!el) return;

        const pinTop = this._readPinTop();
        this._applyPinTopVar(pinTop);
        const navH = this.track ? this.track.offsetHeight : 0;
        const scrollMargin = parseInt(getComputedStyle(el).scrollMarginTop, 10) || 0;
        const pad = 8;
        const top =
          el.getBoundingClientRect().top + window.scrollY - pinTop - navH - scrollMargin - pad;
        window.scrollTo({
          top: Math.max(0, top),
          behavior: this.smooth ? 'smooth' : 'auto',
        });

        this._lockActiveUntilScrollSettles(btn);

        if (!el.hasAttribute('tabindex')) {
          el.setAttribute('tabindex', '-1');
        }
        const focusDelay = this.smooth ? 450 : 0;
        window.setTimeout(() => {
          try {
            el.focus({ preventScroll: true });
          } catch (err) {
            /* ignore */
          }
        }, focusDelay);
      };

      this._updateActiveFromScroll = () => {
        if (this._activeLock || !this.buttons.length) return;
        const pinTop = this._readPinTop();
        const navH = this.track ? this.track.offsetHeight : 0;
        const marker = window.scrollY + pinTop + navH + 12;
        /* 按目标在文档中的位置选高亮，不依赖导航按钮排序。
           取 top <= marker 且 top 最大的一项（最靠近阅读线），
           避免「内容靠前但按钮靠后」时后项覆盖前项（如 Golf Cart vs Home Backup）。 */
        let active = null;
        let bestTop = -Infinity;
        this.buttons.forEach((btn) => {
          const tid = this._parseTargetId(btn.getAttribute('data-anchor-target'));
          const el = tid && document.getElementById(tid);
          if (!el) return;
          const top = el.getBoundingClientRect().top + window.scrollY;
          if (top <= marker && top >= bestTop) {
            bestTop = top;
            active = btn;
          }
        });
        this._setActiveButton(active);
      };

      this._onScrollBound = this._onScroll.bind(this);
      this._onResizeBound = this._onResize.bind(this);
      this._onClickBound = this._onClick.bind(this);

      requestAnimationFrame(() => {
        this._syncLayout();
        this._onScroll();
      });

      window.addEventListener('scroll', this._onScrollBound, { passive: true });
      window.addEventListener('resize', this._onResizeBound);
      /* 只绑 track：吸顶后 track 会移到 body，仍能收到点击 */
      if (this.track) {
        this.track.addEventListener('click', this._onClickBound);
      }
    }

    disconnectedCallback() {
      this._clearActiveLock();
      if (this.pinned) {
        this._setPinned(false);
      }
      window.removeEventListener('scroll', this._onScrollBound);
      window.removeEventListener('resize', this._onResizeBound);
      if (this.track) {
        this.track.removeEventListener('click', this._onClickBound);
      }
    }
  }

  customElements.define('page-anchor-nav', PageAnchorNav);
}
